import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { AUTH_EXPIRED_EVENT, clearAuthStorage } from "../config/authFetch";

interface AuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize authentication state from localStorage synchronously using lazy initializer
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    if (globalThis.window !== undefined) {
      const authStatus = localStorage.getItem("isAuthenticated");
      const email = localStorage.getItem("userEmail");
      return authStatus === "true" && Boolean(email);
    }
    return false;
  });

  const [userEmail, setUserEmail] = useState<string | null>(() => {
    if (globalThis.window !== undefined) {
      return localStorage.getItem("userEmail");
    }
    return null;
  });

  const login = () => {
    const email = localStorage.getItem("userEmail");
    setIsAuthenticated(true);
    setUserEmail(email);
  };

  const logout = useCallback(() => {
    clearAuthStorage();
    setIsAuthenticated(false);
    setUserEmail(null);
  }, []);

  // authFetch fires this when the current environment's token can't be
  // renewed. Storage is already cleared for that environment only, so just
  // drop to the login screen (tokens for the other environment are kept).
  useEffect(() => {
    const onExpired = () => {
      setIsAuthenticated(false);
      setUserEmail(null);
    };
    globalThis.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => globalThis.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const value = useMemo(
    () => ({ isAuthenticated, userEmail, login, logout }),
    [isAuthenticated, userEmail, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
